import React, {useState, useEffect} from 'react';
import {FaCode, FaUser, FaGraduationCap, FaPlay} from 'react-icons/fa';
import Button from '../ui/Button';
import InputField from '../ui/InputField';
import Modal from '../common/Modal';
import axiosClient from '../../services/axiosClient';
import toast from 'react-hot-toast';
import Loader from '../ui/Loader';
import {SelectFieldHookForm} from '../ui/SelectField';
import {compilerOptions} from '../../utils/constants';
import {useForm} from 'react-hook-form';
import OutputWindow from '../codingSubmission/OutputWindow';

const CodingReviewModal = ({
  isOpen,
  onClose,
  assessmentId,
  candidateId,
  problemId,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [score, setScore] = useState('');
  const [adminRemarks, setAdminRemarks] = useState('');

  // Run code functionality states
  const [runCodeLoading, setRunCodeLoading] = useState(false);
  const [outputDetails, setOutputDetails] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [customInput, setCustomInput] = useState('');

  // Form for language selection
  const {control, setValue, watch} = useForm({
    defaultValues: {
      language: null,
    },
  });

  const watchedLanguage = watch('language');

  useEffect(() => {
    if (watchedLanguage) {
      setSelectedLanguage(watchedLanguage);
    }
  }, [watchedLanguage]);

  const fetchCodingSession = async () => {
    try {
      setLoading(true);
      console.log('CodingReviewModal: Fetching coding sessions...');

      // First, get the submitted coding sessions for this assessment and candidate
      const response = await axiosClient.get(
        `/assessment-coding-sessions/assessment/${assessmentId}/submissions`,
        {
          params: {candidateId},
        },
      );

      console.log(
        'CodingReviewModal: Coding sessions response:',
        response.data,
      );
      const codingSessions = response.data?.data?.codingSessions || [];
      console.log('CodingReviewModal: Found sessions:', codingSessions);

      const session = codingSessions.find((s) => s.problemId._id === problemId);
      console.log('CodingReviewModal: Matching session:', session);

      if (session) {
        console.log('CodingReviewModal: Getting detailed session data...');
        // Get detailed session data for review
        const detailResponse = await axiosClient.get(
          `/assessment-coding-sessions/review/${session._id}`,
        );

        console.log('CodingReviewModal: Detail response:', detailResponse.data);
        const sessionDetail = detailResponse.data?.data;
        setSessionData(sessionDetail);
        setScore(sessionDetail.score?.toString() || '');
        setAdminRemarks(sessionDetail.adminRemarks || '');
      } else {
        console.error(
          'CodingReviewModal: No matching session found for problemId:',
          problemId,
        );
        toast.error('Coding submission not found');
      }
    } catch (error) {
      console.error('CodingReviewModal: Error fetching coding session:', error);
      console.error('CodingReviewModal: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      toast.error('Failed to load coding submission');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && assessmentId && candidateId && problemId) {
      console.log('CodingReviewModal: Opening modal with:', {
        assessmentId,
        candidateId,
        problemId,
      });
      fetchCodingSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, assessmentId, candidateId, problemId]);

  // Set default language when session data is loaded
  useEffect(() => {
    if (sessionData && sessionData.language) {
      const defaultLang = compilerOptions.find(
        (lang) => lang.value === sessionData.language,
      );
      if (defaultLang) {
        setValue('language', defaultLang);
        setSelectedLanguage(defaultLang);
      } else {
        // Default to JavaScript if session language is not found
        const jsLang = compilerOptions.find(
          (lang) => lang.value === 'javascript',
        );
        setValue('language', jsLang);
        setSelectedLanguage(jsLang);
      }
    }
  }, [sessionData, setValue]);

  const handleRunCode = async () => {
    if (!sessionData?.sourceCode?.trim()) {
      toast.error('No code available to run');
      return;
    }

    if (!selectedLanguage) {
      toast.error('Please select a programming language');
      return;
    }

    setRunCodeLoading(true);
    setOutputDetails(null);

    try {
      // Get the raw source code
      let sourceCode = sessionData.sourceCode;

      // For query problems, the code is stored as plain text
      if (sessionData?.problem?.is_this_query) {
        sourceCode = sessionData.sourceCode;
      } else {
        // For coding problems, try to decode if it's base64, otherwise use as is
        try {
          // Test if it's valid base64 by checking if it contains only valid base64 characters
          if (
            /^[A-Za-z0-9+/]*={0,2}$/.test(sourceCode) &&
            sourceCode.length % 4 === 0
          ) {
            sourceCode = atob(sourceCode);
          }
        } catch (e) {
          // If decoding fails, use the original code
          console.log('Code is not base64 encoded, using as plain text');
        }
      }

      // Always send the source code as base64 to the backend
      const encodedCode = btoa(sourceCode);

      const result = await axiosClient.post(
        '/assessment-coding-sessions/run-code',
        {
          sourceCode: encodedCode,
          languageId: selectedLanguage.value,
          codingSessionId: sessionData._id,
          stdin: customInput,
        },
      );

      if (result.data?.data) {
        setOutputDetails({
          status: {
            id: result.data.data.status?.id,
            description: result.data.data.status?.description,
          },
          stdout: result.data.data.stdout,
          stderr: result.data.data.stderr,
          compile_output: result.data.data.compile_output,
          time: result.data.data.time,
          memory: result.data.data.memory,
        });

        if (result.data.data.status?.id !== 3) {
          toast.error(
            result.data.data.status?.description ||
              'Code did not execute successfully',
          );
        } else {
          toast.success('Code executed successfully');
        }
      } else {
        toast.error('Failed to execute code');
      }
    } catch (error) {
      console.error('Error running code:', error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to execute code. Please try again.';
      toast.error(errorMessage);

      // Set error output for display
      setOutputDetails({
        status: {
          id: 'error',
          description: 'Execution Error',
        },
        stderr: errorMessage,
        stdout: null,
        compile_output: null,
        time: null,
        memory: null,
      });
    } finally {
      setRunCodeLoading(false);
    }
  };

  const handleSubmitGrade = async () => {
    if (!sessionData) return;

    // Check if score is empty
    if (!score || score.trim() === '') {
      toast.error('Please enter a score before submitting');
      return;
    }

    const scoreValue = parseFloat(score);
    const maxScore = sessionData.maxScore || sessionData.problem.maxScore || 1;

    if (isNaN(scoreValue)) {
      toast.error('Please enter a valid numeric score');
      return;
    }

    if (scoreValue < 0 || scoreValue > maxScore) {
      toast.error(`Score must be between 0 and ${maxScore}`);
      return;
    }

    try {
      setSubmitting(true);

      await axiosClient.put(
        `/assessment-coding-sessions/grade/${sessionData._id}`,
        {
          score: scoreValue,
          adminRemarks,
        },
      );

      toast.success('Coding submission graded successfully');
      onClose();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error grading submission:', error);
      toast.error(
        error.response?.data?.message || 'Failed to grade submission',
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTimeTaken = () => {
    if (!sessionData.startTime || !sessionData.actualEndTime) return 'N/A';

    const start = new Date(sessionData.startTime);
    const end = new Date(sessionData.actualEndTime);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const decodeSourceCode = (encodedCode) => {
    if (!encodedCode) return 'No code submitted';

    try {
      // Test if it's valid base64 by checking pattern and length
      if (
        /^[A-Za-z0-9+/]*={0,2}$/.test(encodedCode) &&
        encodedCode.length % 4 === 0
      ) {
        const decoded = atob(encodedCode);
        return decoded;
      } else {
        // If not base64 pattern, return as is
        return encodedCode;
      }
    } catch (error) {
      // If decoding fails, return the original code
      console.warn('Failed to decode source code:', error);
      return encodedCode;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Code Submission Review & Grading"
      width="full"
      containerClassName="max-w-full max-h-full w-full h-full overflow-hidden p-0"
    >
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader />
          <span className="ml-2 text-gray-600">Loading submission...</span>
        </div>
      ) : sessionData ? (
        <div className="h-[85vh] w-full flex flex-col">
          {/* Top Banner - Candidate Info */}
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex-shrink-0">
            <div className="grid grid-cols-6 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Name:</span>{' '}
                {sessionData.candidate.name}
              </div>
              <div>
                <span className="font-medium text-gray-700">ID:</span>{' '}
                {sessionData.candidate.enrollment || 'N/A'}
              </div>
              <div>
                <span className="font-medium text-gray-700">Email:</span>{' '}
                {sessionData.candidate.email}
              </div>
              <div>
                <span className="font-medium text-gray-700">Submitted:</span>{' '}
                {formatDateTime(sessionData.actualEndTime)}
              </div>
              <div>
                <span className="font-medium text-gray-700">Duration:</span>{' '}
                {getTimeTaken()}
              </div>
              <div>
                <span className="font-medium text-gray-700">Language:</span>{' '}
                {sessionData.language || 'JavaScript'}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Side - Question and Code */}
            <div className="flex-1 flex flex-col">
              <div className="flex h-full">
                {/* Question Panel */}
                <div className="w-1/2 border-r border-gray-200 flex flex-col">
                  <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FaUser className="text-blue-600" size={16} />
                      Question
                    </h3>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-900 mb-3 text-lg">
                        {sessionData.problem.name}
                      </h4>
                      <div className="prose prose-sm max-w-none text-gray-700">
                        <div
                          className="mb-4"
                          dangerouslySetInnerHTML={{
                            __html: sessionData.problem.description,
                          }}
                        />
                        {sessionData.problem.example && (
                          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded-r">
                            <strong className="text-blue-900 block mb-2">
                              Example:
                            </strong>
                            <div
                              className="text-blue-800"
                              dangerouslySetInnerHTML={{
                                __html: sessionData.problem.example,
                              }}
                            />
                          </div>
                        )}
                        {sessionData.problem.constraints && (
                          <div className="bg-green-50 border-l-4 border-green-400 border-o p-4 rounded-r">
                            <strong className="text-green-900 block mb-2">
                              Contraints:
                            </strong>
                            <p
                              className="text-green-800"
                              dangerouslySetInnerHTML={{
                                __html: sessionData.problem.constraints,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Code Panel */}
                <div className="w-1/2 flex flex-col">
                  <div className="p-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                        <FaCode className="text-green-600" size={16} />
                        Code Submission
                        <span className="ml-2 text-xs bg-gray-800 text-white px-2 py-1 rounded font-medium">
                          {sessionData?.problem?.is_this_query
                            ? 'Query'
                            : sessionData.language || 'JavaScript'}
                        </span>
                      </h3>
                    </div>

                    {/* Language Selection and Run Code Controls */}
                    <div className="flex gap-3 items-end">
                      <div className="flex-1">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Test Language:
                        </label>
                        <SelectFieldHookForm
                          control={control}
                          name="language"
                          options={compilerOptions}
                          placeholder="Select language to test"
                          className="w-full"
                        />
                      </div>
                      <Button
                        onClick={handleRunCode}
                        loading={runCodeLoading}
                        disabled={runCodeLoading || !selectedLanguage}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 flex items-center gap-2"
                      >
                        <FaPlay size={12} />
                        Run Code
                      </Button>
                    </div>

                    {/* Custom Input for Testing */}
                    <div className="mt-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Test Input (optional):
                      </label>
                      <textarea
                        value={customInput}
                        onChange={(e) => setCustomInput(e.target.value)}
                        placeholder="Enter custom input for testing..."
                        className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                        rows={2}
                      />
                    </div>
                  </div>

                  {/* Code Display */}
                  <div className="flex-1 flex flex-col overflow-hidden">
                    <div
                      className="flex-1 overflow-auto p-4"
                      style={{backgroundColor: '#1e1e1e'}}
                    >
                      <pre
                        className="text-gray-200 text-sm font-mono leading-relaxed whitespace-pre-wrap m-0"
                        style={{backgroundColor: '#1e1e1e'}}
                      >
                        <code className="text-gray-200">
                          {sessionData?.problem?.is_this_query
                            ? sessionData.sourceCode
                            : decodeSourceCode(sessionData.sourceCode)}
                        </code>
                      </pre>
                    </div>

                    {/* Output Window */}
                    {outputDetails && (
                      <div className="border-t border-gray-300 bg-white flex-shrink-0">
                        <div className="p-3">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">
                            Output
                          </h4>

                          {/* Execution Stats */}
                          {(outputDetails.time ||
                            outputDetails.memory ||
                            outputDetails.status) && (
                            <div className="mb-3 flex gap-4 text-xs">
                              {outputDetails.status?.description && (
                                <span
                                  className={`font-medium px-2 py-1 rounded ${
                                    outputDetails.status.id === 3
                                      ? 'bg-green-100 text-green-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}
                                >
                                  Status: {outputDetails.status.description}
                                </span>
                              )}
                              {outputDetails.time && (
                                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                  ⏱️ {outputDetails.time}s
                                </span>
                              )}
                              {outputDetails.memory && (
                                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                  💾 {outputDetails.memory} KB
                                </span>
                              )}
                            </div>
                          )}

                          <div className="max-h-40 overflow-auto">
                            <OutputWindow outputDetails={outputDetails} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Grading Panel */}
            <div className="w-80 border-l border-gray-200 flex flex-col bg-gray-50">
              <div className="p-4 border-b border-gray-200 bg-white flex-shrink-0">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FaGraduationCap className="text-purple-600" size={16} />
                  Grading
                </h3>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-4">
                  {/* Total Marks Display */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
                    <div className="text-sm text-gray-600 mb-1">
                      Total Marks:
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {sessionData.maxScore ||
                        sessionData.problem?.maxScore ||
                        100}
                    </div>
                  </div>

                  {/* Marks Awarded Input */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Marks Awarded:
                    </label>
                    <InputField
                      type="number"
                      min="0"
                      max={
                        sessionData.maxScore ||
                        sessionData.problem?.maxScore ||
                        100
                      }
                      step={sessionData.maxScore > 1 ? '1' : '0.1'}
                      value={score}
                      onChange={(e) => setScore(e.target.value)}
                      placeholder="0"
                      className="w-full text-center font-semibold text-xl"
                    />
                    <div className="text-xs text-gray-500 mt-2 text-center">
                      Maximum score:{' '}
                      {sessionData.maxScore ||
                        sessionData.problem?.maxScore ||
                        100}
                    </div>
                  </div>

                  {/* Previous Grading Info */}
                  {sessionData.gradedAt && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-sm font-medium text-blue-900 mb-2">
                        Previous Grade:
                      </div>
                      <div className="text-xl font-bold text-blue-800 mb-2">
                        {sessionData.score}/{sessionData.maxScore}
                      </div>
                      <div className="text-xs text-blue-700 mb-2">
                        Graded on {formatDateTime(sessionData.gradedAt)}
                        {sessionData?.gradedBy?.firstName && (
                          <> by {sessionData.gradedBy.firstName}</>
                        )}
                      </div>

                      {sessionData.adminRemarks && (
                        <div className="text-xs text-blue-700 p-2 bg-blue-100 rounded">
                          <strong>Previous Feedback:</strong>
                          <br />
                          {sessionData.adminRemarks}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                <div className="grid grid-cols-2 gap-3">
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={submitting}
                    className="py-2"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSubmitGrade}
                    loading={submitting}
                    disabled={!score || score.trim() === '' || submitting}
                    className="bg-green-600 hover:bg-green-700 text-white font-medium py-2"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            No Submission Found
          </h3>
          <div className="text-sm text-gray-600 space-y-1 mb-4">
            <p>Assessment ID: {assessmentId}</p>
            <p>Candidate ID: {candidateId}</p>
            <p>Problem ID: {problemId}</p>
          </div>
          <p className="text-gray-600 mb-4">
            This candidate may not have submitted a solution yet.
          </p>
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default CodingReviewModal;
